import React, { useEffect, useState } from 'react'
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore'
import { db } from '../../firebase.config'
import { useAuth } from '../../context/AuthContext'
import AppLayout from '../../components/layout/AppLayout'
import ReportModal from '../../components/shared/ReportModal'

const STATUS_COLORS: Record<string, string> = { escalated: '#dc2626', reviewed: '#2563eb', closed: '#16a34a' }

export default function ReportsPage() {
  const { orgId, userData, isForemanOrAbove, isSupervisor } = useAuth()
  const [reports, setReports] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<any>(null)
  const [showModal, setShowModal] = useState(false)
  const [filterEscalated, setFilterEscalated] = useState(false)

  useEffect(() => {
    if (!orgId) return
    const unsub = onSnapshot(
      query(collection(db, 'organizations', orgId, 'dailyReports'), orderBy('createdAt', 'desc')),
      snap => { setReports(snap.docs.map(d => ({ id: d.id, ...d.data() }))); setLoading(false) }
    )
    return () => unsub()
  }, [orgId])

  const handleMarkReviewed = async (reportId: string) => {
    const { doc, updateDoc } = await import('firebase/firestore')
    await updateDoc(doc(db, 'organizations', orgId, 'dailyReports', reportId), {
      reportStatus: 'reviewed',
      reviewedBy: userData?.name || userData?.email,
      reviewedAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    })
  }

  const filtered = filterEscalated ? reports.filter(r => r.escalate) : reports

  const formatDate = (iso: string) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ''

  return (
    <AppLayout title="Daily Reports">
      <div className="mb-6 flex items-center gap-4">
        {isForemanOrAbove() && (
          <button onClick={() => setShowModal(true)}
            className="bg-accent text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-amber-700">
            + New Report
          </button>
        )}
        <input type="text" placeholder="Search reports..." className="border border-gray-300 rounded-lg px-4 py-2 text-sm w-64" />
        {isSupervisor() && (
          <button
            onClick={() => setFilterEscalated(!filterEscalated)}
            className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${filterEscalated ? 'bg-amber-50 border-amber-400 text-amber-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
          >
            🔺 Escalated Only
          </button>
        )}
      </div>

      <div className={selected ? 'grid grid-cols-3 gap-6' : ''}>
        {/* Report List */}
        <div className={selected ? 'col-span-2' : ''}>
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="text-left px-5 py-3 font-medium text-gray-600">Date</th>
                  <th className="text-left px-5 py-3 font-medium text-gray-600">Project</th>
                  <th className="text-left px-5 py-3 font-medium text-gray-600">Submitted By</th>
                  <th className="text-left px-5 py-3 font-medium text-gray-600">Crew</th>
                  {isSupervisor() && <th className="text-left px-5 py-3 font-medium text-gray-600">Escalated</th>}
                  {isSupervisor() && <th className="text-left px-5 py-3 font-medium text-gray-600">Status</th>}
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => (
                  <tr key={r.id}
                    className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${selected?.id === r.id ? 'bg-blue-50' : ''}`}
                    onClick={() => setSelected(r)}>
                    <td className="px-5 py-3 font-medium text-gray-900">{r.date}</td>
                    <td className="px-5 py-3 text-gray-600">{r.projectName || '-'}</td>
                    <td className="px-5 py-3">
                      <span className="text-gray-600">{r.submittedByName || '-'}</span>
                      {r.submittedByRole && (
                        <span className="ml-1 text-xs text-gray-400">({r.submittedByRole})</span>
                      )}
                    </td>
                    <td className="px-5 py-3 text-gray-600">{r.crewOnSite || 0}</td>
                    {isSupervisor() && (
                      <td className="px-5 py-3">
                        {r.escalate && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded font-medium">🔺 Yes</span>}
                      </td>
                    )}
                    {isSupervisor() && (
                      <td className="px-5 py-3">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${r.reportStatus === 'reviewed' ? 'bg-blue-100 text-blue-700' : ''}`}>
                          {r.reportStatus || 'Pending'}
                        </span>
                      </td>
                    )}
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan={isSupervisor() ? 6 : 4} className="px-5 py-12 text-center text-gray-400">No reports found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Report Detail */}
        {selected && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-gray-900">Report — {selected.date}</h3>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-600">✕</button>
            </div>

            {/* Escalation banner */}
            {selected.escalate && (
              <div className="mb-4 bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-center gap-2">
                <span className="text-red-700 font-semibold text-sm">🔺 Escalated for Management Attention</span>
                <span className="text-xs text-red-500">{selected.submittedByName} ({selected.submittedByRole}) flagged this for review</span>
              </div>
            )}

            {/* Work In Progress — highlighted */}
            {selected.workInProgress && (
              <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-1">Work In Progress (Not Complete)</p>
                <p className="text-sm text-gray-700">{selected.workInProgress}</p>
              </div>
            )}

            <div className="space-y-3 text-sm">
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">Project:</span>
                <span className="text-gray-800 font-medium">{selected.projectName || '-'}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">Crew on Site:</span>
                <span className="text-gray-800">{selected.crewOnSite || 0}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium text-gray-600">Submitted by:</span>
                <span className="text-gray-800">{selected.submittedByName || '-'}</span>
              </div>
              {selected.weather && (
                <div className="flex justify-between">
                  <span className="font-medium text-gray-600">Weather:</span>
                  <span className="text-gray-800">{selected.weather}</span>
                </div>
              )}

              {selected.workCompleted && (
                <div>
                  <p className="font-medium text-gray-600 mb-1">Work Completed:</p>
                  <p className="text-gray-800">{selected.workCompleted}</p>
                </div>
              )}

              {selected.equipmentUsed && (
                <div className="flex justify-between">
                  <span className="font-medium text-gray-600">Equipment Used:</span>
                  <span className="text-gray-800">{selected.equipmentUsed}</span>
                </div>
              )}

              {selected.delays && (
                <div className="flex justify-between">
                  <span className="font-medium text-gray-600">Delays:</span>
                  <span className="text-gray-800">{selected.delays}</span>
                </div>
              )}

              {selected.materialsIssues && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
                  <p className="font-semibold text-orange-700 text-xs uppercase tracking-wide mb-1">Materials / Equipment Issues</p>
                  <p className="text-sm text-gray-700">{selected.materialsIssues}</p>
                </div>
              )}

              {selected.safetyIncidents && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="font-semibold text-red-700 text-xs uppercase tracking-wide mb-1">Safety Observations</p>
                  <p className="text-sm text-gray-700">{selected.safetyIncidents}</p>
                </div>
              )}

              {selected.notes && (
                <div className="flex justify-between">
                  <span className="font-medium text-gray-600">Notes:</span>
                  <span className="text-gray-800">{selected.notes}</span>
                </div>
              )}

              {/* Review action for superintendent+ */}
              {isSupervisor() && selected.escalate && selected.reportStatus !== 'reviewed' && (
                <div className="pt-4 border-t border-gray-200">
                  <button
                    onClick={() => handleMarkReviewed(selected.id)}
                    className="w-full bg-blue-600 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700"
                  >
                    Mark as Reviewed
                  </button>
                </div>
              )}
              {isSupervisor() && selected.reportStatus === 'reviewed' && (
                <div className="pt-4 border-t border-gray-200">
                  <span className="flex items-center gap-2 text-sm text-green-600 font-medium justify-center">
                    ✓ Reviewed by {selected.reviewedBy} — {selected.reviewedAt ? formatDate(selected.reviewedAt) : ''}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {showModal && <ReportModal onClose={() => setShowModal(false)} onCreated={() => setShowModal(false)} />}
    </AppLayout>
  )
}